import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";
import { useFormContext, useWatch } from "react-hook-form";
import FilterGroup from "./advanced-filter-builder/FilterGroup";
import { NIYAAZ_COLUMNS, type FilterGroupData } from "./advanced-filter-builder/constants";
import { usePreviewEventRsvpRecipients } from "./hooks";

type EventRsvpRecipientRow = {
  phone?: string;
  memberItsNo?: string;
  hofItsNo?: string;
  recipientName?: string;
  markaz?: string;
  niyaazId?: string;
};

const DEFAULT_FILTER_GROUP: FilterGroupData = {
  id: "root",
  logic: "AND",
  rules: [],
  groups: [],
};

type EventRsvpRecipientPanelProps = {
  onPreviewCount?: (count: number) => void;
  onPreviewed?: (previewed: boolean) => void;
  onSelectionChange?: (
    recipients: { ITS_ID: string | null; phone: string; Full_Name: string | null }[]
  ) => void;
};

export default function EventRsvpRecipientPanel({
  onPreviewCount,
  onPreviewed,
  onSelectionChange,
}: EventRsvpRecipientPanelProps) {
  const { control, setValue } = useFormContext();
  const eventId = useWatch({ control, name: "eventId" }) as string | undefined;
  const filterCriteria = useWatch({ control, name: "filterCriteria" }) as
    | FilterGroupData
    | undefined;
  const [shouldFetch, setShouldFetch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const filterGroup = filterCriteria ?? DEFAULT_FILTER_GROUP;

  const hasFilter = useMemo(
    () => (filterGroup.rules?.length ?? 0) > 0 || (filterGroup.groups?.length ?? 0) > 0,
    [filterGroup]
  );

  const filterCriteriaKey = useMemo(() => JSON.stringify(filterGroup), [filterGroup]);

  const { data, isLoading, error, refetch } = usePreviewEventRsvpRecipients({
    eventId,
    filterCriteria: hasFilter ? filterGroup : undefined,
    limit: 10000,
    offset: 0,
    enabled: shouldFetch,
  });

  const rows = useMemo(() => (data?.data as EventRsvpRecipientRow[] | undefined) ?? [], [data]);
  const total = data?.total ?? 0;

  const handleFilterChange = useCallback(
    (updatedGroup: FilterGroupData) => {
      setValue("filterCriteria", updatedGroup, { shouldDirty: true });
      setShouldFetch(false);
      onPreviewed?.(false);
      onSelectionChange?.([]);
    },
    [setValue, onPreviewed, onSelectionChange]
  );

  const handleFilterReset = useCallback(() => {
    handleFilterChange(DEFAULT_FILTER_GROUP);
  }, [handleFilterChange]);

  useEffect(() => {
    setShouldFetch(false);
    onPreviewed?.(false);
    onSelectionChange?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, filterCriteriaKey]);

  useEffect(() => {
    if (error) {
      onPreviewed?.(false);
      onSelectionChange?.([]);
    } else if (shouldFetch && !isLoading) {
      onPreviewCount?.(total);
      onPreviewed?.(total > 0);

      const selectedRecipients = rows
        .map((row) => {
          const phone = String(row.phone ?? "").trim();
          if (!phone) return null;
          return {
            ...row,
            phone,
            ITS_ID: String(row.memberItsNo ?? row.hofItsNo ?? "").trim() || null,
            Full_Name: String(row.recipientName ?? "").trim() || null,
          };
        })
        .filter((row): row is NonNullable<typeof row> => row !== null);

      onSelectionChange?.(selectedRecipients);
    }
  }, [total, isLoading, error, shouldFetch, rows, onPreviewCount, onPreviewed, onSelectionChange]);

  const handlePreview = useCallback(() => {
    if (!eventId?.trim()) {
      return;
    }
    setShouldFetch(true);
    if (shouldFetch) {
      void refetch();
    }
  }, [eventId, shouldFetch, refetch]);

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase().trim();
    return rows.filter((r) => {
      const name = String(r.recipientName ?? "").toLowerCase();
      const memberIts = String(r.memberItsNo ?? "").toLowerCase();
      const hofIts = String(r.hofItsNo ?? "").toLowerCase();
      const markaz = String(r.markaz ?? "").toLowerCase();
      const phone = String(r.phone ?? "").toLowerCase();
      return (
        name.includes(q) ||
        memberIts.includes(q) ||
        hofIts.includes(q) ||
        markaz.includes(q) ||
        phone.includes(q)
      );
    });
  }, [rows, searchQuery]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));

  if (!eventId?.trim()) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Select a Niyaaz event above to preview registered families and attendees.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
          Niyaaz Registration Audience
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Filter registered families by venue, balance, or chairs. Empty rules = all registered
          attendees.
        </Typography>
      </Box>

      {/* Filter Group Card */}
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
            Niyaaz Form Filter Rules
          </Typography>
          <FilterGroup
            group={filterGroup}
            onUpdate={handleFilterChange}
            onDelete={handleFilterReset}
            level={0}
            availableColumns={NIYAAZ_COLUMNS}
          />
        </CardContent>
      </Card>

      {/* Preview Trigger Bar */}
      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Button
          variant="contained"
          onClick={handlePreview}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ fontWeight: 600, textTransform: "none" }}
        >
          {isLoading
            ? "Loading Recipients..."
            : shouldFetch
              ? "Refresh Preview"
              : "Preview Event Recipients"}
        </Button>

        {shouldFetch && !isLoading && !error && (
          <Chip
            label={`${total} WhatsApp Messages`}
            size="small"
            color="success"
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message || "Failed to preview recipients"}
        </Alert>
      )}

      {shouldFetch && !isLoading && !error && (
        <Box sx={{ width: "100%", minWidth: 0, mt: 2 }}>
          {rows.length > 0 ? (
            <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
              {/* In-Preview Live Search Bar */}
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: "action.hover",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search in preview (Name, ITS, Markaz, Phone)..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchQuery("")}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    width: { xs: "100%", sm: 320 },
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    "& .MuiInputBase-root": { fontSize: "0.8125rem" },
                  }}
                />

                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Showing {filteredRows.length} of {rows.length} records
                </Typography>
              </Box>

              <TableContainer sx={{ maxHeight: 500, overflowX: "auto" }}>
                <Table size="small" stickyHeader sx={{ minWidth: isCompact ? 300 : 550 }}>
                  <TableHead>
                    <TableRow>
                      {isCompact ? (
                        <TableCell sx={{ fontWeight: 600 }}>Recipient Details</TableCell>
                      ) : (
                        <>
                          <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Member Name</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>Member ITS</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>
                            Markaz / Venue
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>
                            WhatsApp Phone
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>HOF ITS</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginated.map((row) => (
                      <TableRow
                        key={`${String(row.niyaazId ?? "")}-${String(row.memberItsNo ?? row.phone ?? "")}`}
                        hover
                      >
                        {isCompact ? (
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                              >
                                {String(row.recipientName ?? "—")}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                                  ITS: {String(row.memberItsNo ?? "—")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  HOF: {String(row.hofItsNo ?? "—")}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                }}
                              >
                                <Chip
                                  label={String(row.markaz ?? "—")}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: "0.68rem" }}
                                />
                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                  {String(row.phone ?? "—")}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                        ) : (
                          <>
                            <TableCell sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
                              {String(row.recipientName ?? "—")}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "0.8125rem",
                                fontFamily: "monospace",
                                fontWeight: 600,
                              }}
                            >
                              {String(row.memberItsNo ?? "—")}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8125rem" }}>
                              <Chip
                                label={String(row.markaz ?? "—")}
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: "0.72rem" }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8125rem" }}>
                              {String(row.phone ?? "—")}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "0.8125rem",
                                fontFamily: "monospace",
                                color: "text.secondary",
                              }}
                            >
                              {String(row.hofItsNo ?? "—")}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filteredRows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Card>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No recipients found matching your Niyaaz filter rules.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
}
